import { motion } from "framer-motion";
import { HelpCircle, MessageCircle, Sparkles, Zap, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { SEO, breadcrumbJsonLd } from "@/lib/seo";

const FAQ = () => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();

    const categories = [
        {
            key: "services" as const,
            icon: <Sparkles className="text-primary" />,
            questionKeys: ["q1", "q2", "q3"],
        },
        {
            key: "timeline" as const,
            icon: <Zap className="text-yellow-500" />,
            questionKeys: ["q1", "q2"],
        },
        {
            key: "support" as const,
            icon: <Shield className="text-success" />,
            questionKeys: ["q1", "q2"],
        },
    ];

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: categories.flatMap((cat) =>
            cat.questionKeys.map((qk) => ({
                "@type": "Question",
                name: t(`faq.categories.${cat.key}.${qk}_q`),
                acceptedAnswer: {
                    "@type": "Answer",
                    text: t(`faq.categories.${cat.key}.${qk}_a`),
                },
            })),
        ),
    };

    return (
        <>
            <SEO
                title={t("meta.faq_title")}
                description={t("meta.faq_description")}
                path="/faq"
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: t("nav.home"), path: "/" },
                        { name: t("nav.faq"), path: "/faq" },
                    ]),
                    faqJsonLd,
                ]}
            />
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh]">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mb-24 text-center mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-black uppercase tracking-widest mb-8"
                        >
                            <HelpCircle size={14} />
                            {t("faq.badge")}
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 tracking-tight"
                        >
                            {t("faq.title_prefix")} <span className="text-muted-foreground">{t("faq.title_highlight")}</span>
                        </motion.h1>
                        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed max-w-2xl mx-auto">
                            {t("faq.subtitle")}
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto space-y-16">
                        {categories.map((cat, idx) => (
                            <motion.div
                                key={cat.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + idx * 0.1 }}
                            >
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                                        {cat.icon}
                                    </div>
                                    <h2 className="text-2xl font-black text-white">
                                        {t(`faq.categories.${cat.key}.title`)}
                                    </h2>
                                </div>

                                <div className="bento-card overflow-hidden">
                                    <Accordion type="single" collapsible className="w-full">
                                        {cat.questionKeys.map((qk, qIdx) => (
                                            <AccordionItem
                                                key={qk}
                                                value={`item-${idx}-${qIdx}`}
                                                className="border-b border-white/5 last:border-0 px-8 py-2"
                                            >
                                                <AccordionTrigger className="text-lg font-bold text-white hover:text-primary hover:no-underline transition-colors py-6">
                                                    {t(`faq.categories.${cat.key}.${qk}_q`)}
                                                </AccordionTrigger>
                                                <AccordionContent className="text-lg text-muted-foreground leading-relaxed pb-8">
                                                    {t(`faq.categories.${cat.key}.${qk}_a`)}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="mt-24 max-w-4xl mx-auto p-12 rounded-[2.5rem] border border-white/5 bg-white/[0.02] flex flex-col md:flex-row items-center gap-8 justify-between"
                    >
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-3xl bg-primary flex items-center justify-center text-white shadow-tech">
                                <MessageCircle size={32} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-black text-white mb-2">{t("faq.cta_title")}</h3>
                                <p className="text-muted-foreground font-medium">{t("faq.cta_subtitle")}</p>
                            </div>
                        </div>
                        <Link to={localizedPath("/contact")}>
                            <Button className="h-14 px-10 bg-white text-background hover:bg-neutral-200 font-black rounded-2xl">
                                {t("faq.cta_button")}
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default FAQ;
